import React, { useState, useCallback, useContext } from "react";
import ReusableActions from "./ReusableActions";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";
import { generateTableColumns } from "../util/ColumnsConfig";
// import { toast } from "react-toastify";
import { useMutation } from "@tanstack/react-query";
import useAsyncState from "../hooks/useAsyncState";
import TableFilters from "./TableFilters";
import { ConfigContext } from "../util/ConfigContext";
import { ThemeContext } from "../util/ThemeContext";
import { ArrowPathIcon } from "@heroicons/react/24/outline";

export const ReusableTable = ({
  tableType,
  tableName,
  needPagination,
  onStateChange,
  needGlobalFilter,
  needColumnFilter,
  actionButtonsMeta,
  isExtendingReusableColumns,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useAsyncState([]);
  const [globalFilter, setGlobalFilter] = React.useState("");
  const [sorting, setSorting] = React.useState([]);
  const [filters, setFilters] = React.useState([]);
  const { jwt, dialogRef } = useContext(ConfigContext);
  const themeData = useContext(ThemeContext);
  const [pagination, setPagination] = React.useState({
    pageIndex: 0,
    pageSize: 10,
  });

  // console.log("JWT in ReusableTable:", jwt);

  const getRowStyle = useCallback(
    (rowData) => {
      if (tableType === "slots") {
        if (rowData.locked === true) {
          return "bg-tealX hover:bg-teal-200 dark:hover:bg-teal-400 border-b dark:bg-tealX dark:border-gray-700 border-gray-200";
        }
      }
      return "even:bg-gray-50 odd:bg-white hover:bg-gray-200 dark:hover:bg-gray-600 bg-white border-b dark:bg-gray-800 dark:border-gray-700 border-gray-200";
    },
    [tableType] // dependency
  );

  const toggleDialog = (action, data = null) => {
    dialogRef.current.updateChildState();
    dialogRef.current.updateCurrentAction(action);
    if (data) {
      dialogRef.current.updateCurrentData(data);
    }
  };

  const mutation = useMutation({
    mutationFn: onStateChange,
    onSuccess: async (response) => {
      setData(response.data); // No need to do response?.data since fetchSubscribers returns data
    },
    onError: (error) => {
      console.error("Error during mutation:", error);
      return error;
    },
  });

  const table = useReactTable({
    data: data?.content ? data?.content : [],
    columns: generateTableColumns(tableType, isExtendingReusableColumns, jwt),
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    onSortingChange: setSorting,
    onColumnFiltersChange: setFilters,
    onPaginationChange: setPagination,
    onGlobalFilterChange: setGlobalFilter,
    state: {
      sorting,
      filters,
      pagination,
      globalFilter,
    },
  });

  const initialPostData = {
    columnFilters: filters,
    globalFilter,
    sorting,
    pagination,
  };

  const prevState = React.useRef({
    sorting,
    filters,
    pagination,
    globalFilter,
  });

  const memoizedOnStateChange = useCallback(
    async (state) => {
      const postData = {
        columnFilters: state.filters,
        globalFilter: state.globalFilter,
        sorting: state.sorting,
        pagination: state.pagination,
      };
      await mutation.mutateAsync(postData);
    },
    [mutation]
  );

  const refreshTableData = async () => {
    try {
      // toast.info("You have been logged out successfully.", {
      //   position: "top-right",
      //   autoClose: 3000,
      //   hideProgressBar: false,
      //   closeOnClick: true,
      //   pauseOnHover: true,
      //   draggable: true,
      //   progress: undefined,
      // });
      setLoading(true);
      await memoizedOnStateChange({
        sorting,
        filters,
        pagination,
        globalFilter,
      });
    } catch (error) {
      setError(`Failed to refresh table data: ${error}`);
    } finally {
      setLoading(false);
    }
  };

  const generateParentActionButtons = (config = [], onClickFn) => {
    return config.map((btn, index) => {
      const isFirst = index === 0;

      const bgColor = isFirst
        ? "bg-blue-600 hover:bg-blue-700"
        : "bg-teal-600 hover:bg-teal-700";

      return (
        <button
          key={btn.action}
          onClick={() => onClickFn(btn.action)}
          className={`rounded-md px-4 py-2 text-white ${bgColor}`}
        >
          {btn.name}
        </button>
      );
    });
  };

  React.useEffect(() => {
    async function fetchInitialData() {
      try {
        await mutation.mutateAsync(initialPostData);
      } catch (error) {
        setError(`Mutation failed: ${error}`);
      } finally {
        setLoading(false);
      }
    }
    fetchInitialData();
  }, []);

  React.useEffect(() => {
    const currentState = { sorting, filters, pagination, globalFilter };
    const hasChanged =
      JSON.stringify(currentState.sorting) !==
        JSON.stringify(prevState.current.sorting) ||
      JSON.stringify(currentState.filters) !==
        JSON.stringify(prevState.current.filters) ||
      JSON.stringify(currentState.pagination) !==
        JSON.stringify(prevState.current.pagination) ||
      currentState.globalFilter !== prevState.current.globalFilter;

    if (hasChanged) {
      prevState.current = currentState;

      const performStateChange = async () => {
        try {
          setLoading(true);
          await memoizedOnStateChange(currentState);
        } catch (error) {
          setError(`Mutation failed: ${error}`);
        } finally {
          setLoading(false);
        }
      };
      performStateChange();
    }
  }, [sorting, filters, pagination, globalFilter, memoizedOnStateChange]);

  return (
    <div className="p-4">
      <div className="flex items-center mb-4 w-full gap-x-4">
        <div className="flex items-center gap-x-4 ml-auto">
          <ReusableActions
            ref={dialogRef}
            refreshTableData={refreshTableData}
          />

          {actionButtonsMeta && (
            <div className="flex items-center gap-x-2">
              {generateParentActionButtons(actionButtonsMeta, toggleDialog)}
            </div>
          )}

          <button
            onClick={refreshTableData}
            className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg shadow hover:bg-blue-600 transition"
          >
            <ArrowPathIcon className="w-5 h-5" />
            Refresh
          </button>
        </div>
      </div>

      <div className="flex justify-center items-center w-full h-10 bg-slate-200 dark:bg-gray-900/80 rounded-lg mb-2 p-6 ">
        <h1 className="md:text-2xl text-xl font-semibold text-gray-700 dark:text-gray-200">
          {tableName}
        </h1>
      </div>

      {(needGlobalFilter || needColumnFilter) && (
        <TableFilters
          needGlobalFilter={needGlobalFilter}
          // className="flex flex-wrap items-center gap-4 bg-gray-50 p-4 rounded-lg shadow"
          globalFilter={globalFilter}
          setGlobalFilter={setGlobalFilter}
          headerGroups={table.getHeaderGroups()}
        />
      )}
      {!loading && data?.content?.length > 0 ? (
        <div
          key={"div_" + tableType}
          className="mt-2 w-full overflow-x-auto relative shadow-md sm:rounded-lg"
        >
          <table
            key={"table_" + tableType}
            className="w-full min-w-max border-collapse border border-gray-300 dark:border-gray-700 text-sm text-center text-gray-500 dark:text-gray-400"
          >
            <thead
              key={"tablehead_" + tableType}
              className="text-xs uppercase bg-gray-500 dark:bg-gray-700 text-gray-100 dark:text-gray-400"
            >
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id + "_" + header.index}
                      colSpan={header.colSpan}
                      className="px-6 py-3 whitespace-nowrap"
                    >
                      {header.isPlaceholder ? null : (
                        <div
                          {...{
                            className: header.column.getCanSort()
                              ? "cursor-pointer select-none"
                              : "",
                            onClick: header.column.getToggleSortingHandler(),
                          }}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          {header.column.getIsSorted() === "asc"
                            ? " 🔼"
                            : header.column.getIsSorted() === "desc"
                            ? " 🔽"
                            : null}
                        </div>
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => {
                return (
                  <tr
                    key={row.original.id}
                    className={getRowStyle(row.original)}
                  >
                    {row.getVisibleCells().map((cell) => {
                      const isActionsColumn =
                        cell.column.columnDef.accessorKey === "actions";
                      const header = String(
                        cell.column.columnDef.header ?? "no-header"
                      ).replace(/\s+/g, "");
                      const accessor =
                        cell.column.columnDef.accessorKey ?? "no-accessor";

                      const key = `cell-${row.index}-${accessor}-${header}`;
                      return (
                        <td
                          key={key}
                          className="border border-gray-300 dark:border-gray-700 px-4 py-2 text-sm text-gray-700 whitespace-nowrap"
                        >
                          {isActionsColumn
                            ? flexRender(cell.column.columnDef.cell, {
                                ...cell.getContext(),
                                row: row.original,
                                callback: toggleDialog,
                                role: jwt?.role,
                                isVideoTenant: jwt?.iv,
                                deviceLockRequired: jwt?.dl,
                                themeData: themeData,
                              })
                            : flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : loading ? (
        <div className="flex flex-col items-center justify-center gap-4 p-6 rounded-xl shadow-md bg-white dark:bg-gray-900 mt-2">
          <div className="animate-spin rounded-full h-14 w-14 border-t-4  border-opacity-30"></div>
          <h1 className="text-lg font-semibold text-gray-700 dark:text-gray-200">
            Loading content...
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Please wait while we fetch your data.
          </p>
        </div>
      ) : error ? (
        <div className="p-6 rounded-xl shadow-md bg-red-50 dark:bg-red-950 text-center mt-2">
          <div className="text-3xl">❌</div>
          <h1 className="mt-3 text-xl font-bold text-red-600 dark:text-red-400">
            Something went wrong
          </h1>
          <p className="mt-1 text-sm text-red-500 dark:text-red-300">{error}</p>
        </div>
      ) : (
        <div className="p-6 rounded-xl shadow-md bg-gray-50 dark:bg-gray-800 text-center mt-2">
          <div className="text-3xl">📭</div>
          <h1 className="mt-3 text-xl font-semibold text-gray-700 dark:text-gray-200">
            No Content Found
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Try adjusting your search or filters.
          </p>
        </div>
      )}
      <div className="h-2" />
      {needPagination && !loading && (
        <div className="flex flex-wrap items-center gap-4 bg-slate-200 dark:bg-gray-900/80 p-4 rounded-lg shadow">
          <div className="flex items-center gap-2">
            <button
              key={"first_" + tableType}
              className={`px-3 py-2 text-sm rounded-lg shadow transition 
                ${
                  data?.first
                    ? "bg-gray-300 text-gray-500 dark:bg-gray-700 dark:text-gray-400 cursor-not-allowed"
                    : "bg-blue-500 text-white hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700"
                }`}
              onClick={() => table.setPageIndex(0)}
              disabled={data?.first}
            >
              {"<<"}
            </button>
            <button
              key={"prev_" + tableType}
              className={`px-3 py-2 text-sm rounded-lg shadow transition 
                  ${
                    data?.first
                      ? "bg-gray-300 text-gray-500 dark:bg-gray-700 dark:text-gray-400 cursor-not-allowed"
                      : "bg-blue-500 text-white hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700"
                  }`}
              onClick={() => table.previousPage()}
              disabled={data?.first}
            >
              {"Previous"}
            </button>
            <button
              key={"next_" + tableType}
              className={`px-3 py-2 text-sm rounded-lg shadow transition 
                  ${
                    data?.last
                      ? "bg-gray-300 text-gray-500 dark:bg-gray-700 dark:text-gray-400 cursor-not-allowed"
                      : "bg-blue-500 text-white hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700"
                  }`}
              onClick={() => table.nextPage()}
              disabled={data?.last}
            >
              {"Next"}
            </button>
            <button
              key={"last_" + tableType}
              className={`px-3 py-2 text-sm rounded-lg shadow transition 
                  ${
                    data?.last
                      ? "bg-gray-300 text-gray-500 dark:bg-gray-700 dark:text-gray-400 cursor-not-allowed"
                      : "bg-blue-500 text-white hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700"
                  }`}
              onClick={() => table.setPageIndex(data?.totalPages - 1)}
              disabled={data?.last}
            >
              {">>"}
            </button>
          </div>
          <div className="text-gray-700 dark:text-gray-200">
            Page <strong>{table.getState().pagination.pageIndex + 1}</strong> of{" "}
            <strong>{data?.totalPages}</strong>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-gray-700 dark:text-gray-200">
              |&nbsp; Go to page:
            </span>
            <input
              type="number"
              min="1"
              max={data?.totalPages ?? 1}
              defaultValue={table.getState().pagination.pageIndex + 1}
              onChange={(e) => {
                const input = Number(e.target.value);
                const totalPages = data?.totalPages ?? 1;
                const clampedPage = Math.min(Math.max(input, 1), totalPages);
                table.setPageIndex(clampedPage - 1);
              }}
              className="w-14 px-2 py-1 text-center border rounded-lg 
               bg-white text-gray-800 
               dark:bg-black dark:text-white 
               border-gray-300 dark:border-gray-700 
               focus:outline-none focus:ring focus:ring-blue-300 dark:focus:ring-blue-500"
            />
          </div>
          <div>
            <select
              value={table.getState().pagination.pageSize}
              onChange={(e) => table.setPageSize(Number(e.target.value))}
              className="w-32 px-3 py-2 border rounded-lg text-sm 
               bg-white text-gray-800 
               dark:bg-black dark:text-white 
               border-gray-300 dark:border-gray-700 
               focus:outline-none focus:ring focus:ring-blue-300 dark:focus:ring-blue-500"
            >
              {[5, 10, 20, 30, 40, 50].map((pageSize) => (
                <option
                  key={pageSize}
                  value={pageSize}
                  className="text-sm text-gray-800 dark:text-slate-300 bg-white dark:bg-black"
                >
                  Show {pageSize}
                </option>
              ))}
            </select>
          </div>

          <div>
            {data?.pageable ? (
              <h1 className="text-gray-700 dark:text-gray-200">
                Showing: {data.pageable.offset + 1}-
                {data.pageable.offset + data.numberOfElements} of&nbsp;
                {data.totalElements}
              </h1>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReusableTable;

/* <pre className="justify-self-start">
        {JSON.stringify(
          {
            columnFilters: table.getState().filters,
            globalFilter: table.getState().globalFilter,
            sorting: table.getState().sorting,
            pagination: table.getState().pagination,
          },
          null,
          2
        )}
      </pre> */
